import { HttpRequestMethodTypes, JSON_CONTENT_TYPE_HEADER } from 'types';
import { fetchWithTimeout } from 'utils/fetchWithTimeout';
import type { FetchOptions } from 'types/fetchWithRetry';
import type {
  AgentTranslationContent,
  AgentTranslationRequestBody,
  AgentTranslationResponse,
} from 'pages/api/agent-translation/types';

/**
 * Default timeout for translation requests (2 seconds as per requirements)
 */
export const TRANSLATION_TIMEOUT = 10000;

/**
 * Query key factory for agent translation queries
 * Uses agent ID as the cache key - much simpler and more efficient than hashing content
 */
export const agentTranslationKeys = {
  all: ['agent-translation'] as const,
  translation: (agentId: string, targetLanguage: string, sourceLanguage: string) =>
    [...agentTranslationKeys.all, 'translate', agentId, targetLanguage, sourceLanguage] as const,
};

/**
 * Translates agent profile content using the translation API
 * 
 * @param content - The agent content to translate (bio, specializations, designations)
 * @param targetLanguage - The target language code (e.g., 'fr-CA', 'fr', 'es')
 * @param sourceLanguage - The source language code (defaults to 'en')
 * @param signal - Optional AbortSignal for cancellation
 * @returns Translated content or throws an error
 */
export async function translateAgentContent(
  content: AgentTranslationContent,
  targetLanguage: string,
  sourceLanguage: string = 'en',
  signal?: AbortSignal
): Promise<AgentTranslationContent> {
  // Early return if nothing to translate
  if (!content.bio && !content.specializations?.length && !content.designations?.length) {
    return content;
  }

  // Don't call API if source and target are the same
  if (sourceLanguage === targetLanguage) {
    return content;
  }

  const body: AgentTranslationRequestBody = {
    content,
    targetLanguage,
    sourceLanguage,
  };

  const options: FetchOptions = {
    headers: JSON_CONTENT_TYPE_HEADER,
    method: HttpRequestMethodTypes.POST,
    body: JSON.stringify(body),
    signal,
  };

  try {
    const response = await fetchWithTimeout(
      '/api/agent-translation/translate',
      options,
      TRANSLATION_TIMEOUT
    );

    const parsedResponse = await response.json();

    if (response.ok) {
      const { translatedContent } = parsedResponse as AgentTranslationResponse;
      return translatedContent || content;
    } else if ('error' in parsedResponse) {
      const { error } = parsedResponse as AgentTranslationResponse;
      throw new Error(error?.message || `Request failed with status ${response.status}`);
    }
  } catch (error) {
    // Re-throw abort errors as-is for proper react-query handling
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    
    const wrappedError = new Error(
      `Translation request failed: ${(error as Error)?.message}`,
      { cause: error as Error }
    );
    console.error(wrappedError);
    throw wrappedError;
  }

  throw new Error('Translation request failed');
}

